import { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { Section } from '../data/portfolioData';

interface PlanetMarkerProps {
  section: Section;
  isSelected: boolean;
  onClick: () => void;
}

export default function PlanetMarker({ section, isSelected, onClick }: PlanetMarkerProps) {
  const groupRef = useRef<THREE.Group>(null);
  const ringRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);
  const pulseRef = useRef(0);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    pulseRef.current = t;

    if (ringRef.current) {
      ringRef.current.rotation.z = t * 2;
      const scale = 1 + Math.sin(t * 3) * 0.15;
      ringRef.current.scale.setScalar(isSelected ? 1.5 : scale);
    }

    if (groupRef.current) {
      groupRef.current.lookAt(0, 0, 0);
    }
  });

  const color = new THREE.Color(section.color);

  return (
    <group ref={groupRef} position={section.position}>
      <mesh
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        onPointerEnter={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerLeave={() => {
          setHovered(false);
          document.body.style.cursor = 'default';
        }}
      >
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshBasicMaterial color={section.color} />
      </mesh>

      <pointLight color={section.color} intensity={isSelected ? 2 : 0.5} distance={1.5} />

      <mesh ref={ringRef}>
        <ringGeometry args={[0.12, 0.15, 32]} />
        <meshBasicMaterial
          color={section.color}
          transparent
          opacity={isSelected ? 0.8 : 0.4}
          side={THREE.DoubleSide}
        />
      </mesh>

      <mesh scale={isSelected ? 2.5 : hovered ? 1.8 : 1.2}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshBasicMaterial color={section.color} transparent opacity={0.1} />
      </mesh>

      {hovered && !isSelected && (
        <Html
          center
          distanceFactor={8}
          style={{ pointerEvents: 'none' }}
        >
          <div
            style={{
              background: 'rgba(5,5,16,0.92)',
              border: `1px solid ${section.color}60`,
              borderRadius: '8px',
              padding: '6px 14px',
              color: '#fff',
              fontSize: '13px',
              fontFamily: 'Inter, sans-serif',
              whiteSpace: 'nowrap',
              backdropFilter: 'blur(10px)',
              boxShadow: `0 0 15px ${section.color}40`,
              transform: 'translateY(-30px)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '10px',
                color: section.color,
                letterSpacing: '0.1em',
              }}
            >
              {section.code}
            </span>
            {section.title}
          </div>
        </Html>
      )}
    </group>
  );
}
